import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { REVIEWS } from "@/data/reviews";

/**
 * ReviewsCarousel — carosello elegante delle recensioni Google.
 * Una recensione alla volta; testi lunghi troncati con "Leggi tutto".
 * Prop `compact`: padding e corpo ridotti (es. pagina Chi Sono).
 */
export default function ReviewsCarousel({ compact = false }) {
  const [[index, direction], setState] = useState([0, 1]);
  const [expanded, setExpanded] = useState(false);
  const total = REVIEWS.length;
  const go = (d) => { setExpanded(false); setState(([i]) => [(i + d + total) % total, d]); };
  const review = REVIEWS[index];
  const isLong = review.text.length > 320;

  return (
    <div data-testid="reviews-carousel" className="relative">
      <div className={`bg-white border border-[#323C36]/10 shadow-sm relative overflow-hidden ${compact ? "px-5 py-8 sm:px-10 sm:py-9" : "px-6 py-10 sm:px-12 sm:py-12"}`}>
        <span aria-hidden="true" className="absolute -top-4 left-8 font-display text-[120px] leading-none text-[#A8674F]/15 select-none">"</span>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.figure
            key={index}
            custom={direction}
            initial={{ opacity: 0, x: direction * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -40 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-center gap-1 mb-5" aria-label={`${review.stars} stelle su 5`}>
              {[...Array(5)].map((_, s) => (
                <Star
                  key={s}
                  size={15}
                  className={s < review.stars ? "fill-[#E8A050] text-[#E8A050]" : "text-[#323C36]/20"}
                />
              ))}
            </div>
            <blockquote
              data-testid="review-quote"
              className={`font-display leading-relaxed text-[#2A3229] max-w-2xl mx-auto ${compact ? "text-sm sm:text-base" : "text-base sm:text-lg"} ${isLong && !expanded ? "line-clamp-5" : ""}`}
            >
              "{review.text}"
            </blockquote>
            {isLong && (
              <button
                data-testid="review-expand"
                onClick={() => setExpanded((e) => !e)}
                className="mt-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#A8674F] hover:text-[#8A5240] transition-colors"
              >
                {expanded ? "Mostra meno" : "Leggi tutto"}
              </button>
            )}
            <figcaption className="mt-6">
              <p data-testid="review-author" className="text-sm uppercase tracking-[0.22em] font-semibold text-[#323C36]">
                {review.name}
              </p>
              {review.company && (
                <p data-testid="review-company" className="text-sm text-[#A8674F] mt-1">{review.company}</p>
              )}
            </figcaption>
          </motion.figure>
        </AnimatePresence>
      </div>

      {total > 1 && (
        <div className="mt-5 flex items-center justify-center gap-6">
          <button
            data-testid="review-prev"
            onClick={() => go(-1)}
            aria-label="Recensione precedente"
            className="w-10 h-10 rounded-full border border-[#323C36]/30 text-[#323C36] flex items-center justify-center transition-all duration-300 hover:bg-[#323C36] hover:text-[#F6F3EF]"
          >
            <ChevronLeft size={17} />
          </button>
          <div className="flex items-center gap-2">
            {REVIEWS.map((_, d) => (
              <button
                key={d}
                data-testid={`review-dot-${d}`}
                onClick={() => { setExpanded(false); setState([d, d > index ? 1 : -1]); }}
                aria-label={`Vai alla recensione ${d + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${d === index ? "w-6 bg-[#A8674F]" : "w-1.5 bg-[#323C36]/25 hover:bg-[#323C36]/50"}`}
              />
            ))}
          </div>
          <button
            data-testid="review-next"
            onClick={() => go(1)}
            aria-label="Recensione successiva"
            className="w-10 h-10 rounded-full border border-[#323C36]/30 text-[#323C36] flex items-center justify-center transition-all duration-300 hover:bg-[#323C36] hover:text-[#F6F3EF]"
          >
            <ChevronRight size={17} />
          </button>
        </div>
      )}
    </div>
  );
}
